export const LEGACY_LOGIN_CONTRACT_FALLBACK = "legacy_contract_fallback" as const;
export const MAX_LOGIN_RETRY_DELAY_SECONDS = 15 * 60;

export const LOGIN_RECOVERY_ACTIONS = {
    "LOGIN-REQUEST-INVALID": "RESTART_SIGN_IN",
    "LOGIN-PIN-INVALID": "RETRY_PIN",
    "LOGIN-PIN-EXPIRED": "REQUEST_NEW_CODE",
    "LOGIN-PIN-ATTEMPTS-EXCEEDED": "REQUEST_NEW_CODE",
    "LOGIN-CODE-RATE-LIMITED": "WAIT",
    "LOGIN-ACCOUNT-BLOCKED": "CONTACT_SUPPORT",
    "LOGIN-IDENTITY-FAILED": "RETRY_LATER",
    "LOGIN-INTERNAL-ERROR": "RETRY_LATER",
} as const;

export type LoginRecoveryStatus = keyof typeof LOGIN_RECOVERY_ACTIONS;
export type LoginRecoveryAction =
    (typeof LOGIN_RECOVERY_ACTIONS)[LoginRecoveryStatus];
export type LoginContract = "canonical" | typeof LEGACY_LOGIN_CONTRACT_FALLBACK;

export interface LoginSuccess {
    success: true;
}

export interface LoginRecoveryFailure {
    success: false;
    status: LoginRecoveryStatus;
    recoveryAction: LoginRecoveryAction;
    retryAfterSeconds?: number;
    contract: LoginContract;
}

export type LoginResult = LoginSuccess | LoginRecoveryFailure;
export type LoginOperation = "login" | "request";

interface FailureEnvelope {
    httpStatus?: number;
    body: Record<string, unknown>;
}

const asRecord = (value: unknown): Record<string, unknown> =>
    value !== null && typeof value === "object"
        ? (value as Record<string, unknown>)
        : {};

const failureEnvelope = (failure: unknown): FailureEnvelope => {
    const candidate = asRecord(failure);
    const response = asRecord(candidate.response);
    if (Object.keys(response).length > 0) {
        return {
            httpStatus:
                typeof response.status === "number" ? response.status : undefined,
            body: asRecord(response.data),
        };
    }

    return {
        httpStatus:
            typeof candidate.status === "number" ? candidate.status : undefined,
        body: candidate,
    };
};

export const boundedRetryAfterSeconds = (value: unknown): number => {
    const parsed = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(parsed)) {
        return 1;
    }
    return Math.min(
        MAX_LOGIN_RETRY_DELAY_SECONDS,
        Math.max(1, Math.ceil(parsed))
    );
};

const resultFor = (
    status: LoginRecoveryStatus,
    contract: LoginContract,
    retryAfterSeconds?: unknown
): LoginRecoveryFailure => {
    const result: LoginRecoveryFailure = {
        success: false,
        status,
        recoveryAction: LOGIN_RECOVERY_ACTIONS[status],
        contract,
    };
    if (status === "LOGIN-CODE-RATE-LIMITED") {
        result.retryAfterSeconds = boundedRetryAfterSeconds(retryAfterSeconds);
    }
    return result;
};

const normalizedText = (...values: unknown[]): string =>
    values
        .filter((value): value is string => typeof value === "string")
        .join(" ")
        .toLowerCase();

/**
 * The only compatibility boundary for pre-contract API bodies. New reason
 * codes always win; legacy shapes are translated here and are explicitly
 * tagged so telemetry can identify when the fallback is still in use.
 */
export const resolveLoginRecoveryFailure = (
    failure: unknown,
    operation: LoginOperation
): LoginRecoveryFailure => {
    const { httpStatus, body } = failureEnvelope(failure);
    const data = asRecord(body.data);
    const status = data.status;
    if (
        typeof status === "string" &&
        Object.prototype.hasOwnProperty.call(LOGIN_RECOVERY_ACTIONS, status)
    ) {
        return resultFor(
            status as LoginRecoveryStatus,
            "canonical",
            data.retryAfterSeconds
        );
    }

    const legacyText = normalizedText(
        body.message,
        body.DisplayText,
        body.ErrorMessage,
        typeof body.data === "string" ? body.data : undefined
    );
    const legacy = LEGACY_LOGIN_CONTRACT_FALLBACK;

    if (httpStatus === 400 && (body.DisplayText || body.ErrorMessage)) {
        return resultFor("LOGIN-REQUEST-INVALID", legacy);
    }
    if (legacyText.includes("blocked")) {
        return resultFor("LOGIN-ACCOUNT-BLOCKED", legacy);
    }
    if (operation === "login") {
        if (legacyText.includes("expired")) {
            return resultFor("LOGIN-PIN-EXPIRED", legacy);
        }
        if (
            legacyText.includes("incorrect pin") ||
            legacyText.includes("invalid pin") ||
            (httpStatus === 400 && body.data === null)
        ) {
            return resultFor("LOGIN-PIN-INVALID", legacy);
        }
        if (httpStatus === 400) {
            return resultFor("LOGIN-REQUEST-INVALID", legacy);
        }
        if (httpStatus === 429) {
            return resultFor("LOGIN-PIN-ATTEMPTS-EXCEEDED", legacy);
        }
        if (httpStatus === 401) {
            return resultFor("LOGIN-IDENTITY-FAILED", legacy);
        }
    } else {
        if (httpStatus === 400) {
            return resultFor("LOGIN-REQUEST-INVALID", legacy);
        }
        if (httpStatus === 429) {
            return resultFor(
                "LOGIN-CODE-RATE-LIMITED",
                legacy,
                data.retryAfterSeconds ?? body.retryAfterSeconds
            );
        }
        if (httpStatus === 401) {
            return resultFor("LOGIN-IDENTITY-FAILED", legacy);
        }
    }

    return resultFor("LOGIN-INTERNAL-ERROR", legacy);
};

export const isExactFourDigitPin = (value: string): boolean => /^\d{4}$/.test(value);

/** Player-facing wording for a login-recovery failure, shared by every auth page. */
export const recoveryMessage = (result: LoginRecoveryFailure): string => {
    switch (result.status) {
        case "LOGIN-REQUEST-INVALID":
            return "Please sign in again to continue.";
        case "LOGIN-PIN-INVALID":
            return "Invalid PIN. Please try again.";
        case "LOGIN-PIN-EXPIRED":
            return "This PIN has expired. Request a new code to continue.";
        case "LOGIN-PIN-ATTEMPTS-EXCEEDED":
            return "Too many PIN attempts. Request a new code to continue.";
        case "LOGIN-CODE-RATE-LIMITED":
            return `Please wait ${result.retryAfterSeconds || 1} seconds before requesting another code.`;
        case "LOGIN-ACCOUNT-BLOCKED":
            return "This account is locked. Please contact support.";
        case "LOGIN-IDENTITY-FAILED":
        case "LOGIN-INTERNAL-ERROR":
            return "Sign-in is temporarily unavailable. Please try again shortly.";
    }
};
